import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Save, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { accountingApi } from "@/lib/apiClient";

const SOURCE = { manual: "set by you", code: "same code", name: "same name" };

export const XeroMapping = ({ onChanged, provider = "xero", label = "Xero", unit = "account" }) => {
  const apiRef = React.useRef(accountingApi(provider));
  const px = apiRef.current;
  const [d, setD] = useState(null);
  const [edits, setEdits] = useState({});
  const [q, setQ] = useState("");
  const [onlyUnmapped, setOnlyUnmapped] = useState(true);
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [aiKeys, setAiKeys] = useState({});
  useEffect(() => { px.mapping().then(setD).catch((e) => toast.error(e?.response?.data?.detail || `Could not load ${label} ${unit}s`)); }, [px, label, unit]);
  if (!d) return <div className="py-6 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>;

  const rows = d.rows.filter((r) => (!onlyUnmapped || !r.xero_code || edits[r.code] !== undefined) && (!q || `${r.code} ${r.name}`.toLowerCase().includes(q.toLowerCase())));
  const dirty = Object.keys(edits).length;
  const suggest = () => {
    setSuggesting(true);
    px.suggest().then((r) => {
      const next = {}; const keys = {};
      Object.entries(r.suggestions).forEach(([k, v]) => { next[k] = v.xero_code; keys[k] = true; });
      setEdits((m) => ({ ...m, ...next })); setAiKeys(keys);
      const n = Object.keys(next).length;
      toast[n ? "success" : "message"](n ? `AI suggested ${n} of ${r.unmapped} — review, then Save` : "AI found no confident matches");
    }).catch((e) => toast.error(e?.response?.data?.detail || "AI suggestion failed")).finally(() => setSuggesting(false));
  };
  const save = () => {
    setSaving(true);
    px.saveMapping(edits).then((r) => { setD(r); setEdits({}); setAiKeys({}); toast.success("Mapping saved"); onChanged?.(); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Save failed")).finally(() => setSaving(false));
  };
  return (
    <div className="rounded-lg border border-border p-4" data-testid={`${provider}-mapping`}>
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <div className="font-medium">D20 GL code → {label} {unit} <span className="text-muted-foreground">({d.tenant_name})</span></div>
        <label className="ml-auto flex items-center gap-1.5 text-xs"><input type="checkbox" checked={onlyUnmapped} onChange={(e) => setOnlyUnmapped(e.target.checked)} data-testid="xero-mapping-only-unmapped" /> only unmapped ({d.unmapped})</label>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search code or name" className="h-8 w-48" data-testid="xero-mapping-search" />
        {d.unmapped > 0 && px.suggest && <Button size="sm" variant="outline" disabled={suggesting} onClick={suggest} data-testid="xero-mapping-suggest">{suggesting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />} Suggest with AI</Button>}
        <Button size="sm" disabled={!dirty || saving} onClick={save} data-testid="xero-mapping-save">{saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />} Save{dirty ? ` (${dirty})` : ""}</Button>
      </div>
      <p className="text-xs text-muted-foreground mt-1">Codes or names that already exist in {label} match automatically. Pick a {label} {unit} for the rest — bills with an unmatched code are rejected by {label}.</p>
      {rows.length === 0 ? <p className="mt-4 text-sm text-emerald-500" data-testid="xero-mapping-empty">Every GL code has a {label} {unit}.</p> : (
        <div className="mt-3 max-h-80 overflow-auto d20-scroll">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground"><tr className="text-left"><th className="py-1.5 pr-3">D20 code</th><th className="py-1.5 pr-3">Name</th><th className="py-1.5 pr-3">{label} {unit}</th><th className="py-1.5">How</th></tr></thead>
            <tbody>
              {rows.map((r) => {
                const val = edits[r.code] !== undefined ? edits[r.code] : (r.xero_code || "");
                return (
                  <tr key={r.code} className="border-t border-border" data-testid={`xero-map-row-${r.code}`}>
                    <td className="py-1.5 pr-3 font-mono">{r.code}{r.used && <span className="ml-1.5 rounded bg-amber-500/15 px-1 py-0.5 font-sans text-[10px] font-semibold text-amber-600 dark:text-amber-400" title="Used on approved invoices waiting to be sent" data-testid={`map-used-${r.code}`}>used</span>}</td>
                    <td className="py-1.5 pr-3 truncate max-w-[220px]">{r.name}</td>
                    <td className="py-1.5 pr-3">
                      <select value={val} onChange={(e) => setEdits((m) => ({ ...m, [r.code]: e.target.value || null }))} data-testid={`xero-map-select-${r.code}`}
                              className={cn("h-8 w-full max-w-[280px] rounded-md border bg-background px-2 text-sm", !val && "border-amber-500/60")}>
                        <option value="">— not mapped —</option>
                        {d.xero_accounts.map((a) => <option key={a.code} value={a.code}>{provider === "xero" ? `${a.code} · ${a.name}` : a.name}</option>)}
                      </select>
                    </td>
                    <td className="py-1.5 text-xs text-muted-foreground">{edits[r.code] !== undefined ? (aiKeys[r.code] ? "AI suggestion · unsaved" : "unsaved") : SOURCE[r.source] || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
